import * as React from "react";
import _ from "lodash";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import { Tooltip } from "src/library/components/Tooltip";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import {
  IProcessJob,
  ProcessJobErrorPayloads,
  ProcessJobPayloads,
  ProcessJobStatus
} from "src/core/process-job/api/processJobs.api";

import { ProcessJobStatusDisplay } from "../ProcessJobStatus";

export interface IProcessJobTableColumns {
  actionButton?: (info: CellContext<IProcessJob, unknown>) => React.ReactNode;
  hasExpand?: boolean;
  showName: boolean;
  isOrderImport?: boolean;
}

interface ProcessJobStatusSelectOption {
  text: string;
  value: ProcessJobStatus;
}

export const processJobsTableColumns = (params: IProcessJobTableColumns) => {
  const customColumnHelper = customCreateColumnHelper<IProcessJob>();
  const columnHelper = createColumnHelper<IProcessJob>();

  // Select dropdown options for status column
  const processJobStatuses: ProcessJobStatusSelectOption[] = [
    {
      value: "success",
      text: "Success"
    },
    {
      value: "error",
      text: "Error"
    },
    {
      value: "part_error",
      text: "Part Error"
    },
    {
      value: "queued",
      text: "Queued"
    },
    {
      value: "in_progress",
      text: "In Progress"
    },
    {
      value: "initialising",
      text: "Initialising"
    }
  ];

  const columns = [
    customColumnHelper.standardTextColumn(columnHelper, "status", {
      header: "Status",
      enableColumnFilter: true,
      enableSorting: false,
      minSize: 100,
      meta: {
        filter: {
          filterType: "select",
          options: processJobStatuses.map(status => ({
            text: status.text,
            value: status.value
          }))
        }
      },
      cell: row => <ProcessJobStatusDisplay status={row.getValue() as ProcessJobStatus} />
    }),
    customColumnHelper.dateColumn(columnHelper, "addedAt", {
      header: "Added At",
      template: "datetime"
    }),
    customColumnHelper.standardTextColumn(columnHelper, "statusMessage", {
      header: "Details",
      enableColumnFilter: true,
      enableSorting: false,
      size: 350
    }),
    customColumnHelper.numberColumn(columnHelper, "totalCreated", {
      header: "Created",
      enableColumnFilter: false,
      enableSorting: false
    }),
    customColumnHelper.numberColumn(columnHelper, "totalUpdated", {
      header: "Updated",
      enableColumnFilter: false,
      enableSorting: false
    })
  ];

  if (params.showName) {
    columns.unshift(customColumnHelper.standardTextColumn(columnHelper, "name", { enableColumnFilter: true }));
  }

  if (params.isOrderImport) {
    columns.splice(3, 0, customColumnHelper.dateColumn(columnHelper, "autoRetry", { header: "Auto retry" }));

    columns.unshift(
      customColumnHelper.standardTextColumn(columnHelper, "jobData", {
        header: "File Name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: row => {
          const rowValue = row.getValue() as ProcessJobPayloads["import-file-order"];
          const filePathSplit = rowValue.s3Key ? rowValue.s3Key.split("/") : [];

          return (
            <Text>
              {filePathSplit.length ? filePathSplit[ filePathSplit.length - 1 ] : ""}
            </Text>
          );
        }
      })
    );

    // shows any data we have from errors in a tooltip
    columns.unshift(customColumnHelper.standardTextColumn(columnHelper, "errors", {
      header: "Data",
      enableColumnFilter: false,
      enableSorting: false,
      size: 50,
      minSize: 20,
      meta: { style: { align: "center" } },
      cell: ({ row }) => {
        if (row.original?.errors && row.original?.errors[ 0 ]) {
          const rowData = row.original?.errors[ 0 ].data as ProcessJobErrorPayloads["import-file-order"];

          // No point showing tool tip if no data to display
          if (!rowData) {
            return null;
          }

          return (
            <>
              <Icon
                size={15}
                icon={Icons.INFO}
                data-tip
                data-for={`${row.original.id}-error-data`}
              />

              <Tooltip
                id={`${row.original.id}-error-data`}
                type="dark"
                place="top"
                getContent={() => {
                  return _.keys(rowData).map(key => {
                    const rowDataValue = rowData[ key ];

                    // Don't attempt to display values that are objects in tool tip
                    if (typeof rowDataValue === "object") {
                      return null;
                    }

                    return (
                      <>
                        <Text
                          key={`${row.original.id}-${key}`}
                          color="white"
                        >
                          {`${key}: ${rowDataValue}`}
                        </Text>

                        <br />
                      </>
                    );
                  });
                }}
              />
            </>
          );
        }

        return null;
      }
    }));

    if (params.actionButton) {
      columns.push(customColumnHelper.actionColumn(columnHelper, params.actionButton, { size: 200 }));
    }
  }

  return columns as Array<ColumnDef<IProcessJob, keyof IProcessJob>>;
};
